import { TranslationService } from '../../../shared/translation/translation.service';
import { NotificationService } from '../../../shared/notification/notification.service';
import { UnappliedPaymentService } from '../../shared/unapplied-payment/unapplied-payment.service';
import { UnappliedPaymentDto, initializedUnappliedPaymentDto } from '../../shared/unapplied-payment/dto/unapplied-payment.dto';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { PaymentSource } from '../../shared/payment-source/payment-source.model';
import { PaymentSourceService } from '../../shared/payment-source/payment-source.service';
import { BatchService } from '../../shared/invoice/batch.service';
import { PaymentTypeService } from '../../shared/payment-types/payment-type.service';
import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { CreditCardPaymentInfo } from '../../shared/credit-card/credit-card-payment-info.model';
import { CreditCardInputComponent } from '../../credit-card-input/credit-card-input.component';
import { CreditCardGateway } from '../../shared/credit-card-gateway/credit-card-gateway.model';
import { CreditCardGatewayService } from '../../shared/credit-card-gateway/credit-card-gateway.service';
import { PrimeNgDropdownEvent } from 'src/app/shared/primeng/events/dropdown-event';
import { CreditCardService } from '../../shared/credit-card/credit-card.service';
import { CreditCardRefundRequest } from '../../shared/credit-card/credit-card-refund-request.model';
import { PrecznService } from 'src/app/integrations/preczn/shared/preczn.service';
import { TokenizeGatewayDto } from '../../shared/gateway-resolution/dto/tokenize-gateway.dto';
@Component({
  selector: 'wl-create-unapplied-payment-dialog',
  templateUrl: './create-unapplied-payment-dialog.component.html',
  styleUrls: ['./create-unapplied-payment-dialog.component.less'],
  providers: [PaymentSourceService],
    standalone: false
})
export class CreateUnappliedPaymentDialogComponent implements OnInit {
  @ViewChild(CreditCardInputComponent) private _creditCardInputComponent: CreditCardInputComponent;
  @Input()
  get show() {
    return this._show;
  }
  set show(value: boolean) {
    this._show = value;
    if (value) {
      this.initPayment();
    }
    this.showChange.emit(this._show);
  }
  @Input() profileId: number;
  @Output() showChange: EventEmitter<boolean> = new EventEmitter();
  @Output() paymentCreated: EventEmitter<any> = new EventEmitter();
  paymentTypes: PaymentTypeDto[];
  paymentSources: PaymentSource[];
  unappliedPayment: UnappliedPaymentDto;
  creditCardPayment: CreditCardPaymentInfo;
  isSaving: boolean;
  // Direct-gateway surface: resolved from the paying CCGatewayID the selected payment type already
  // carries, not from revenue-item resolution.
  resolvedGateway: TokenizeGatewayDto | null = null;

  get creditCardPaymentIsValid() {
    if (!this.creditCardPayment) {
      return true;
    }
    if (this._creditCardInputComponent?.usePreczn) {
      return this.creditCardPayment.Amount > 0;
    }
    return (
      this.creditCardPayment.CardType &&
      this.creditCardPayment.Amount > 0 &&
      this.creditCardPayment.ValidThruMonth &&
      this.creditCardPayment.ValidThruYear &&
      this.creditCardPayment.CardNum &&
      this.creditCardPayment.SecurityCode
    );
  }

  private _show: boolean;
  private _cardGateways: CreditCardGateway[];

  constructor(
    private _paymentTypeService: PaymentTypeService,
    private _batchService: BatchService,
    private _paymentSourceService: PaymentSourceService,
    private _unappliedPaymentService: UnappliedPaymentService,
    private _notificationService: NotificationService,
    private _creditCardGatewayService: CreditCardGatewayService,
    private _creditCardService: CreditCardService,
    private _translationService: TranslationService,
    private _precznService: PrecznService
  ) {}

  ngOnInit() {
    this.initPayment(true);

    this._paymentTypeService.getPaymentTypes().then((data) => {
      this.paymentTypes = data;
    });

    this._paymentSourceService.getPaymentSources().then((data) => {
      this.paymentSources = data;
    });

    this._creditCardGatewayService.getCreditCardGateways().then((data) => {
      this._cardGateways = data;
    });
  }

  get selectedPaymentType() {
    return this.unappliedPayment._paymentType;
  }
  set selectedPaymentType(value: PaymentTypeDto) {
    if (value) {
      this.unappliedPayment._paymentType = value;
      this.unappliedPayment.PaymentType = value.PaymentType;
      this.unappliedPayment.PaymentTypeId = value.PaymentTypeId;
      if (!this.creditCardPayment && value.CreditCardType) {
        this.creditCardPayment = new CreditCardPaymentInfo();
        this.creditCardPayment.ProfileId = this.profileId;
      } else if (!value.CreditCardType) {
        this.creditCardPayment = null;
        this.resolvedGateway = null;
      }

      if (this.creditCardPayment) {
        this.updateCcGateway(value);
        this.updateUseCardConnect();
      }
    }
  }

  get selectedPaymentSource() {
    return this.unappliedPayment._paymentSource;
  }
  set selectedPaymentSource(value: PaymentSource) {
    if (value) {
      this.unappliedPayment._paymentSource = value;
      this.unappliedPayment.PaymentSource = value.PaymentSource;
    }
  }

  async saveClicked() {
    if (this.creditCardPayment) {
      this.creditCardPayment.Amount = this.unappliedPayment.Amount;
      if (!this.creditCardPaymentIsValid) {
        this._notificationService.translateError('revenue.transactions-grid.unapplied-payments.create.invalid-cc-info');
        return;
      }
      if (this._creditCardInputComponent?.usePreczn) {
        try {
          await this._creditCardInputComponent.prepareForSubmit();
        } catch (e) {
          this._notificationService.error(e?.message ?? this._translationService.getInstant('revenue.credit-card-input.tokenize-error'));
          return;
        }
      }
    }
    this.isSaving = true;
    let ccPromise;
    if (this.selectedPaymentType.CreditCardType) {
      this._creditCardService.fillCcFieldsWithCcInfo(this.creditCardPayment, this.unappliedPayment);
      ccPromise = this._creditCardService.processCreditCardPayment(this.creditCardPayment).then((result) => {
        if (result.Succeeded || this.creditCardPayment.CardNum === '411111111111111') {
          this.unappliedPayment.CC_CreditCardNum = result.CcId;
        } else {
          this._notificationService.translateError('revenue.transactions-grid.unapplied-payments.create.failed-cc-payment');
        }
      });
    } else {
      ccPromise = Promise.resolve();
    }

    ccPromise
      .then(() => {
        this._unappliedPaymentService
          .create(this.unappliedPayment)
          .then(() => {
            this._notificationService.translateSuccess('revenue.transactions-grid.unapplied-payments.create.success-message');
            this.paymentCreated.emit();
            this.show = false;
          })
          .catch((err) => {
            this._notificationService.httpError(err);
            const refund = new CreditCardRefundRequest();
            refund.CreditCardTransactionId = +this.unappliedPayment.CC_CreditCardNum?.replace('ccid_', '');
            refund.CreateAdjustments = false;
            refund.RefundAmount = this.creditCardPayment.Amount;
            this._creditCardService.refundCreditCardPayment(refund);
          });
      })
      .catch((err) => {
        this._notificationService.httpError(err);
      })
      .finally(() => (this.isSaving = false));
  }

  private initPayment(initial = false) {
    this.unappliedPayment = initializedUnappliedPaymentDto();
    this.unappliedPayment.ProfileId = this.profileId;
    if (!initial) {
      this._batchService.getNextBatchNumber().then((data) => {
        this.unappliedPayment.Batch_Creation = data;
      });
    }
  }

  private updateCcGateway(paymentType: PaymentTypeDto) {
    this.paymentTypes.forEach((type) => {
      if (type.PaymentTypeId == paymentType.PaymentTypeId) {
        this.creditCardPayment.CCGatewayID = type.CcGatewayId;
      }
    });
    this.updateResolvedGateway();
  }

  // The widget here is mounted unconditionally (behind [hidden]/cardNotApplicable rather than
  // *ngIf), so it loads the SDK before any payment type has been picked; this is what retargets it
  // once the gateway is known.
  private updateResolvedGateway(): void {
    this._precznService.resolveTokenizeGateway(this.creditCardPayment?.CCGatewayID ?? 0).then((gateway) => {
      this.resolvedGateway = gateway;
    });
  }

  private updateUseCardConnect() {
    this._cardGateways.forEach((gateway) => {
      if (this.creditCardPayment.CCGatewayID == gateway.CCGatewayID) {
        if (gateway.GatewayTypeID == 6) {
          this._creditCardInputComponent.useCardConnect = true;
        } else {
          this._creditCardInputComponent.useCardConnect = false;
        }
      }
    });
  }
}
